import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Header from '../../components/navigation/Header';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Icon from '../../components/AppIcon';

import ClientSelectionPanel from './components/ClientSelectionPanel';
import ViolationTriggerInterface from './components/ViolationTriggerInterface';
import NotificationTrackingDashboard from './components/NotificationTrackingDashboard';
import TestResultsPanel from './components/TestResultsPanel';
import { workforceManagementService } from '../../services/workforceManagementService';
import { employeeService } from '../../services/employeeService';
import { violationDetectionService } from '../../services/violationDetectionService';
import { notificationEngineService } from '../../services/notificationEngineService';
import realtimeViolationService from '../../services/realtimeViolationService';

const ViolationSimulatorTestingControlCenter = () => {
  const [loading, setLoading] = useState(true);
  const [selectedClients, setSelectedClients] = useState([]);
  const [availableClients, setAvailableClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [testResults, setTestResults] = useState([]);
  const [notificationLogs, setNotificationLogs] = useState([]);
  const [activeTest, setActiveTest] = useState(null);
  const [realtimeChannel, setRealtimeChannel] = useState(null);
  const [notificationChannel, setNotificationChannel] = useState(null);

  useEffect(() => {
    loadInitialData();
    setupRealtimeMonitoring();

    return () => {
      cleanupSubscriptions();
    };
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [company, employeeData] = await Promise.all([
        workforceManagementService?.getActiveCompany(),
        employeeService?.getAll()
      ]);

      // For now, use the active company as the primary client
      // In a multi-tenant system, this would fetch multiple organizations
      if (company) {
        setAvailableClients([{
          id: company?.id,
          name: company?.companyName,
          employeeCount: employeeData?.length || 0,
          status: 'active',
          lastTestDate: null
        }]);
      }

      setEmployees(employeeData);
    } catch (error) {
      console.error('Failed to load initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeMonitoring = () => {
    // Subscribe to violation detection logs
    const violationChannel = realtimeViolationService?.subscribeToViolationDetectionLog(
      (detectionLog) => {
        console.log('Real-time violation detected:', detectionLog);
        setTestResults(prev => [{
          id: detectionLog?.id,
          timestamp: detectionLog?.detectionTimestamp,
          triggerType: detectionLog?.triggerType,
          detectionSource: detectionLog?.detectionSource,
          alertSent: detectionLog?.alertSent,
          adminNotified: detectionLog?.adminNotified,
          clientNotified: detectionLog?.clientNotified,
          affectedEmployeeIds: detectionLog?.affectedEmployeeIds,
          status: 'completed'
        }, ...prev]);
      }
    );
    setRealtimeChannel(violationChannel);
  };

  const cleanupSubscriptions = () => {
    if (realtimeChannel) {
      realtimeViolationService?.unsubscribe(realtimeChannel);
    }
    if (notificationChannel) {
      realtimeViolationService?.unsubscribe(notificationChannel);
    }
  };

  const handleTriggerViolation = async (violationConfig) => {
    try {
      setActiveTest({
        id: Date.now(),
        status: 'running',
        startTime: new Date()?.toISOString(),
        config: violationConfig
      });

      // Create test violation in the database
      const violation = await violationDetectionService?.createViolation({
        violationType: violationConfig?.triggerType,
        severity: violationConfig?.severity,
        description: `[TEST] ${violationConfig?.description}`,
        department: violationConfig?.department,
        affectedCount: violationConfig?.affectedEmployeeIds?.length || 0,
        affectedEmployeeIds: violationConfig?.affectedEmployeeIds || [],
        estimatedCost: violationConfig?.estimatedCost || 0,
        regulationReference: violationConfig?.regulationReference || 'Test Scenario',
        violationStatus: 'identified'
      });

      // Create instant violation alert
      await violationDetectionService?.createInstantViolationAlert(
        violation?.id,
        violationConfig?.triggerType,
        violationConfig?.affectedEmployeeIds || [],
        violationConfig?.estimatedCost || 0
      );

      // Track notification delivery
      const notifications = await notificationEngineService?.getPendingNotifications();
      setNotificationLogs(prev => [...notifications, ...prev]);

      setActiveTest(prev => ({
        ...prev,
        status: 'completed',
        endTime: new Date()?.toISOString(),
        violationId: violation?.id
      }));

    } catch (error) {
      console.error('Failed to trigger test violation:', error);
      setActiveTest(prev => ({
        ...prev,
        status: 'failed',
        error: error?.message
      }));
    }
  };

  const handleBulkTest = async (configs) => {
    for (const config of configs) {
      await handleTriggerViolation(config);
      // Add delay between tests
      await new Promise(resolve => setTimeout(resolve, 2000));
    }
  };

  const handleClearResults = () => {
    setTestResults([]);
    setNotificationLogs([]);
    setActiveTest(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <ComplianceAlertBanner />
      <DataRefreshIndicator />
      <main className="pt-20 pb-12 px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Page Header */}
          <div className="mb-6 md:mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Icon name="TestTube2" size={32} color="var(--color-primary)" />
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground">
                Violation Simulator & Testing Control Center
              </h1>
            </div>
            <p className="caption text-sm md:text-base text-muted-foreground">
              Manually trigger test violations and verify real-time push notifications across multiple clients
            </p>
          </div>

          {/* Status Banner */}
          {activeTest && (
            <div className={`mb-6 p-4 rounded-lg border ${
              activeTest?.status === 'running' ? 'bg-blue-50 border-blue-200' :
              activeTest?.status === 'completed'? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-center gap-3">
                {activeTest?.status === 'running' && (
                  <div className="w-4 h-4 rounded-full bg-blue-500 animate-pulse" />
                )}
                <div className="flex-1">
                  <p className="font-medium text-foreground">
                    {activeTest?.status === 'running' && 'Test in progress...'}
                    {activeTest?.status === 'completed' && 'Test completed successfully'}
                    {activeTest?.status === 'failed' && 'Test failed'}
                  </p>
                  {activeTest?.error && (
                    <p className="text-sm text-error mt-1">{activeTest?.error}</p>
                  )}
                </div>
                {activeTest?.status === 'completed' && (
                  <Icon name="CheckCircle2" size={24} color="var(--color-success)" />
                )}
              </div>
            </div>
          )}

          {/* Main Grid Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Client Selection Panel - 4 cols */}
            <div className="lg:col-span-4">
              <ClientSelectionPanel
                clients={availableClients}
                selectedClients={selectedClients}
                onSelectionChange={setSelectedClients}
                loading={loading}
              />
            </div>

            {/* Violation Trigger Interface - 8 cols */}
            <div className="lg:col-span-8">
              <ViolationTriggerInterface
                employees={employees}
                selectedClients={selectedClients}
                onTriggerViolation={handleTriggerViolation}
                onBulkTest={handleBulkTest}
                activeTest={activeTest}
              />
            </div>
          </div>

          {/* Notification Tracking Dashboard */}
          <div className="mt-6">
            <NotificationTrackingDashboard
              notificationLogs={notificationLogs}
              testResults={testResults}
              selectedClients={selectedClients}
            />
          </div>

          {/* Test Results Panel */}
          <div className="mt-6">
            <TestResultsPanel
              testResults={testResults}
              onClearResults={handleClearResults}
            />
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default ViolationSimulatorTestingControlCenter;